/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	Toast,
	Toaster,
	ToastTitle,
	useId,
	useToastController,
	Dialog,
	DialogTrigger,
	DialogSurface,
	DialogBody,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	makeStyles,
	tokens
} from '@fluentui/react-components';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useAzureProvider } from 'src/azure/context';
import { getAutomationRuleListService } from 'src/services/appgami-api/get-automation-rule-list-service';
import { identitiesList } from './utils/list-identities';
import { UpdateAutomationRuleDataType, updateAutomationRulesService } from 'src/services/appgami-api/update-automation-rules-service';
import { AutomationRuleType } from 'src/services/appgami-api/types/automation-rule';
import { deleteAutomationRuleByIdService } from 'src/services/appgami-api/delete-automation-rule';
import { findAutomationRuleSettingsService } from 'src/services/appgami-api/find-automation-rule-settings';

export type AutomationRuleItem = AutomationRuleType;

export type NameCell = {
	label: string;
};

export type StatusCell = {
	label: string;
};

export type AuthorCell = {
	name: string;
	image: string;
	mail: string;
};

export type CreatedAtCell = {
	date: Date;
}

export type Item = {
	automationRuleItem: AutomationRuleItem;
	name: NameCell;
	status: StatusCell;
	author: AuthorCell;
	createdAt: CreatedAtCell;
};

export interface AutomationRuleListContextType {
  list: Array<Item>;
  setList: React.Dispatch<React.SetStateAction<Item[]>>;
  selectedItems: Array<string>;
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
	openDeleteAutomationRuleModalHandler(): void;
  deleteAutomationRule(idList: Array<string>): Promise<void>;
	loadAutomationRuleList(): Promise<void>;
	updateAutomationRules(automationRuleList: Array<UpdateAutomationRuleDataType>): Promise<void>;
	loadingAutomationRuleList: boolean;
	setLoadingAutomationRuleList:  React.Dispatch<React.SetStateAction<boolean>>;

	authorList: Array<{ label: string; value: string }>;
	authorListQuery: string;
	setAuthorListQuery: React.Dispatch<React.SetStateAction<string>>;
	justOneToDelete: string | null;
	setJustOneToDelete: React.Dispatch<React.SetStateAction<string | null>>;
}

export const AutomationRuleListContext = createContext({} as AutomationRuleListContextType);
export const useAutomationRuleListController = () => useContext(AutomationRuleListContext);

export default function AutomationRuleListProvider(props: { children?: ReactNode | Iterable<ReactNode> }) {
	const classes = useStyles();

	const toasterId = useId('toasterId');
	const { dispatchToast } = useToastController(toasterId);

	const { applicationContext } = useAzureProvider();

	const [loadingAutomationRuleList, setLoadingAutomationRuleList] = useState(true);
	const [openDelModal, setOpenDelModal] = useState(false);

	const [list, setList] = useState<Array<Item>>([]);
	const [authorListQuery, setAuthorListQuery] = useState('');
	const [authorList, setAuthorList] = useState<Array<{ label: string; value: string }>>([]);

	const [justOneToDelete, setJustOneToDelete] = useState<string | null>(null);
	const [selectedItems, setSelectedItems] = useState<Array<string>>([]);

	const loadAutomationRuleList = useCallback(async () => {
		try {
			const data = await getAutomationRuleListService({
				organizationName: applicationContext.organizationName,
				projectId: applicationContext.projectId
			});

			if (data) {
				const identities = identitiesList();
				const values: Array<Item> = await Promise.all(
					data
						.map(async (automation) => {
							await identities.search(automation.created_by);
							const identity: any = identities.getIdentity(automation.created_by);
							
							return {
								automationRuleItem: automation,
								name: { label: automation.name },
								status: { label: automation.status },
								author: {
									name: identity.displayName,
									image: `https://dev.azure.com/${identity.image}`,
									mail: identity.mail
								},
								createdAt: {
									date: automation.created_at,
								}
							};
						})
				);
				
				setAuthorList(identities
					.getList()
					.map((identity: any) => ({
						label: identity.displayName,
						value: identity.localId
					}))
				);
				const sortedList = values.sort((a, b) => a.automationRuleItem.name.localeCompare(b.automationRuleItem.name, undefined, { sensitivity: 'base' }));
				setList(sortedList);
				setLoadingAutomationRuleList(false);
			} else {
				setList([]);
				setLoadingAutomationRuleList(false);
			}
		} catch (err) {
			// error handler
			setLoadingAutomationRuleList(false);
		}
	}, [applicationContext]);

	const openDeleteAutomationRuleModalHandler = useCallback(() => {
		setOpenDelModal(oldValue => !oldValue);
	}, []);
	
	const deleteAutomationRule = useCallback(async (idList: Array<string>) => {
		await deleteAutomationRuleByIdService({
			projectId: applicationContext.projectId,
			organizationName: applicationContext.organizationName,
			automationIdList: idList,
		});

		dispatchToast(
			<Toast>
				<ToastTitle>Deleted automation rule with success.</ToastTitle>
			</Toast>,
			{ position: 'bottom-end', intent: 'success', timeout: 3000 }
		);

		setList([]);
		setSelectedItems([]);
		loadAutomationRuleList();
	}, [applicationContext]);

	const updateAutomationRules = useCallback(async (automationRuleList: Array<UpdateAutomationRuleDataType>) => {
		try {
			setLoadingAutomationRuleList(true);
			const ruleListWithActor = async (rule: UpdateAutomationRuleDataType) => {
				const foundAutomations = await findAutomationRuleSettingsService({
					projectId: applicationContext.projectId,
					organizationName: applicationContext.organizationName,
					automationRuleId: rule.id,
				});

				if (foundAutomations.type === 'success') {
					return {
						...rule,
						actor: foundAutomations.data.settings.actor.type as 'user' | 'service-principal',
					};
				}

				return {
					...rule,
					actor: 'user' as 'user' | 'service-principal',
				};
			};

			const response = await updateAutomationRulesService({
				organizationName: applicationContext.organizationName,
				projectId: applicationContext.projectId,
				automationRuleList: await Promise.all(automationRuleList.map(ruleListWithActor))
			});

			if (response && response.status === 403) {
				dispatchToast(
					<Toast>
						<ToastTitle>You don&apos;t have permission to update automation rules in this project.</ToastTitle>
					</Toast>,
					{ position: 'bottom-end', intent: 'error', timeout: 3000 }
				);
			}

			if (response && response.status === 400) {
				dispatchToast(
					<Toast>
						<ToastTitle>There was an error updating the automation rules.</ToastTitle>
					</Toast>,
					{ position: 'bottom-end', intent: 'error', timeout: 3000 }
				);
			}

			if (response && response.status === 204) {
				dispatchToast(
					<Toast>
						<ToastTitle>Automation rules updated with success.</ToastTitle>
					</Toast>,
					{ position: 'bottom-end', intent: 'success', timeout: 3000 }
				);
			}

			setList([]);
			setSelectedItems([]);

			// mostrar toast de sucesso
			await loadAutomationRuleList();
		} catch(err) {
			// mostrar toast de erro
			setList([]);
			setSelectedItems([]);
			await loadAutomationRuleList();
			console.error(err);
		}
	}, [applicationContext]);

	const deleteItemMessage = useMemo(() => {
		if (justOneToDelete) {
			const item = list.find(listItem => listItem.automationRuleItem.id === justOneToDelete)?.automationRuleItem?.name || '';

			return (
				<span className={classes.automationRuleDeleteText}>
					Are you sure you want to delete{' '}
					<span className={classes.automationRuleDeleteTextStrong}>
						{item}
					</span>
				? This action cannot be undone.
				</span>
			);
		}

		if (!justOneToDelete && selectedItems.length > 1) {
			return (
				<ul style={{listStyleType: 'disc', listStylePosition: 'inside'}}>
					<span className={classes.automationRuleDeleteText}>Are you sure you want to delete these automations? This action cannot be undone.</span>
					{selectedItems.map((item, idx) => {
						return (
							<li key={idx}>
								<span className={classes.automationRuleDeleteTextStrong}>
									{list.find(listItem => listItem.automationRuleItem.id === item)?.automationRuleItem?.name || ''}
								</span>
							</li>
						);
					})}
				</ul>
			);
		}

		const item = list.find(listItem => listItem.automationRuleItem.id === selectedItems[0])?.automationRuleItem?.name || '';

		return (
			<span className={classes.automationRuleDeleteText}>
				Are you sure you want to delete{' '}
				<span className={classes.automationRuleDeleteTextStrong}>
					{item}
				</span>
				? This action cannot be undone.
			</span>
		);
	}, [selectedItems, justOneToDelete, list, props]);

	return (
		<AutomationRuleListContext.Provider
			value={{
				list,
				setList,
				selectedItems,
				setSelectedItems,
				deleteAutomationRule,
				loadAutomationRuleList,
				openDeleteAutomationRuleModalHandler,
				loadingAutomationRuleList,
				setLoadingAutomationRuleList,
				authorList,
				updateAutomationRules,
				authorListQuery,
				setAuthorListQuery,
				justOneToDelete,
				setJustOneToDelete
			}}
		>
			<Toaster toasterId={toasterId} />
			<>
				{props?.children}
			</>
			<>
				<Dialog modalType="alert" open={openDelModal}>
					<DialogSurface >
						<DialogBody>
							<DialogTitle>Delete Automation Rule</DialogTitle>
							<DialogContent>
								{deleteItemMessage}
							</DialogContent>
							<DialogActions>
								<DialogTrigger disableButtonEnhancement>
									<Button
										appearance="secondary"
										onClick={() => {
											openDeleteAutomationRuleModalHandler();
											setTimeout(() => {
												setJustOneToDelete(null);
											}, 250);
										}}
									>
										Close
									</Button>
								</DialogTrigger>
								<DialogTrigger>
									<Button
										appearance="primary"
										onClick={async () => {
											if (justOneToDelete) {
												openDeleteAutomationRuleModalHandler();

												setTimeout(() => {
													setJustOneToDelete(null);
													deleteAutomationRule([justOneToDelete]);
												}, 250);
											}

											if (!justOneToDelete) {
												openDeleteAutomationRuleModalHandler();
												setTimeout(() => {
													deleteAutomationRule(selectedItems);
												}, 250);
											}
										}}
									>
										Confirm
									</Button>
								</DialogTrigger>
							</DialogActions>
						</DialogBody>
					</DialogSurface>
				</Dialog>
			</>
		</AutomationRuleListContext.Provider>
	);
}

export const useStyles = makeStyles({
	automationRuleDeleteText: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase400,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase400,
	},
	automationRuleDeleteTextStrong: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase300,
		fontWeight: tokens.fontWeightSemibold,
		lineHeight: tokens.lineHeightBase400,
	}
});